import React from "react";
import { BCMSCurrency, DisabledPrompt, Link, useStateManager, BrandingStyle } from "bcms-react";

import CustomAccordion, { CustomAccordionContent, CustomAccordionToggle } from "components/custom-ui/CustomAccordion";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import UIRadioGroup, { UIRadio } from "components/custom-ui/UIRadioGroup";
import { FormContainer, FormRowView } from "components/shared";
import FormButtonRow from "components/shared/containers/FormContainer";
import IconToggle from "components/shared/views/IconToggle";
import { PrimaryButton, SecondaryButton, UIAmountInput, UINumberInput, UITextInput } from "components/ui";
import { SVGAsset } from "res";

import "./mobile.css";
import "./styles.css";
import { UITextClearInput } from "components/ui/UITextInput";
import Util from "classes/Util";

/**
 * @function TransactionSearchAccordion
 * @param {OBJECT} controller - The controller passed from the AccountDetailsPage to update the shown transactions based on the filter
 * @return {React.Component}
 * @exported true
 * @description This allows the user to search transactions for each account.
 * @example 
 * <TransactionSearchAccordion />
 */
export default function TransactionSearchAccordion(props) {
    const { ui, onSearchAccordionClick } = useStateManager();

    return (
        <div className="transaction-search-accordion">
            <h2><span data-point="Account_Details_Transactions">Transactions</span></h2>
            <div className="flex-container align-top">
                <CustomAccordion isOpen={ ui.searchAccordion.isActive }>
                    <div className="flex-container flex-horizontal align-center transaction-search-options">
                        <CustomAccordionToggle className={ Util.inMabi ? "search.no-border" : "search" } onClick={ onSearchAccordionClick }>
                            <IconToggle icon="MagnifyingGlassIcon" text="Search" dataPoint="Search"/>
                        </CustomAccordionToggle>

                        {/* The "Download" link is not shown in MABI23, but it is shown in ABI22 desktop and responsive. */}
                        { !Util.inMabi && 
                        <DisabledPrompt>
                              <BrandingStyle className="flex-container set-width align-center download clickable highlighted-text link" style={{"color" : "links_abi22"}} element={
                            <div >
                              <BrandingStyle style={{"fill" : "links_abi22"}} element={<SVGAsset id="DownloadArrowIcon" />}/>
                          <p data-point="Download">Download</p>
                            </div>
                            }/>
                        </DisabledPrompt>
                        }
                    </div>
                    
                    <TransactionSearchContent />
                </CustomAccordion>
            </div>
        </div>
    );
}

function TransactionSearchContent(props) {
    const { data, ui, onSearchUpdate, onSearchAmountUpdate, onSearchReset, onSearchSubmit, onSearchByCheck, onSearchAccordionClick } = useStateManager();

    const search = data.search;
    const amount = search.amount === "" || BCMSCurrency.toDecimal(search.amount) === "NaN"
        ? undefined
        : BCMSCurrency.toDecimal(search.amount);

    const generateOptions = obj => [...obj].map(([ key, value ]) => {
        const item = <CustomSelectItem key={ `Item_${ key }` } data-point={ value.replace(/ /g, "_") } value={ key }>{ value }</CustomSelectItem>;
        return key !== "custom" ? item : <DisabledPrompt key={ `Disabled_${ key }` }>{ item }</DisabledPrompt>;
    }, []);

    const dateOptions = generateOptions(search.ranges);
    const transactionOptions = generateOptions(search.types);

    return (
        <CustomAccordionContent className="transaction-search-content flex-container flex-vertical">
          <CustomAccordionToggle className="flex-container flex-width close-accordion" onClick={ onSearchAccordionClick }>
            <BrandingStyle className="flex-width" style={{"color" : "search_text_abi22"} } element={<p >SEARCH</p>}/>
            <SVGAsset className="icon set-width" id="CloseX" data-point="CloseButton" />
          </CustomAccordionToggle>
          <FormContainer className="small-form-width">
            <FormRowView text="Transaction Type" dataPoint="Transaction_Type">
              <CustomSelect data-point="Transaction_Type" value={ search.type } onChange={ onSearchUpdate("type") } active={ ui.searchType.isActive }>
                { transactionOptions }
              </CustomSelect>
            </FormRowView>
            <FormRowView text="Date Range" dataPoint="Date_Range">
              <CustomSelect data-point="Data_Range" value={ search.date } onChange={ onSearchUpdate("date") } active={ ui.searchDateRange.isActive }>
                { dateOptions }
              </CustomSelect>
            </FormRowView>
            <FormRowView text="Description" dataPoint="Description">
              <UITextClearInput data-point="Description_Text_Input" value={ search.description } onBlur={ onSearchUpdate("description") }/>
            </FormRowView>
    
            { ui.searchByCheck &&
              <>
                <FormRowView>
                  <UIRadioGroup value="amount" direction="horizontal"> 
                      <UIRadio value="amount" label="Amount" dataPoint="Transaction_Search_Amount_Radio" />
                      <DisabledPrompt>
                        <UIRadio value="range" label="Amount Range" dataPoint="Transaction_Search_Amount_Range_Radio" />
                      </DisabledPrompt>
                  </UIRadioGroup>
                </FormRowView>
                <FormRowView text="Amount">
                  <UIAmountInput value={ amount ?? "" } onBlur={ onSearchAmountUpdate } formatEmpty={ false } data-point="Transaction_Search_Amount_Field" />
                </FormRowView>
                <FormRowView text="Check Number">
                  <UINumberInput value={ search.check } onBlur={ onSearchUpdate("check") } dataPoint="Transaction_Search_Check_Number_Field" />
                </FormRowView>
              </> }
    
            { !ui.searchByCheck &&
              <FormRowView>
                <a className="clickable" data-point="Search_By_Amount_Check_Number" onClick={ onSearchByCheck }>Search by Amount & Check Number</a>
              </FormRowView> }
            
            <FormButtonRow className="search-accordion-buttons">
              <SecondaryButton text="Clear" onClick={ onSearchReset } />
              <PrimaryButton text="Search" data-point="Search_Button" onClick={ onSearchSubmit } />
            </FormButtonRow>
          </FormContainer>
        </CustomAccordionContent>
      );
}